import { useIsAuthenticated, useMsal } from '@azure/msal-react';
import { IconButton, Tooltip, Typography } from '@mui/material';
import Box from '@mui/material/Box';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import { LogOut } from 'lucide-react';
import { Fragment } from 'react';
import { FormattedMessage } from 'react-intl';
import { useRecoilState } from 'recoil';
import ConnectAzure from '~/components/DicomUpload/AzureDicomDownload/ConnectAzure';
import LocalFilesExplorerTab from '~/components/DicomUpload/LocalUpload/LocalFilesTab';
import useProcessDicomFiles from '~/hooks/useProcessDicomFiles';
import useViewportEnvironment from '~/hooks/useViewportEnvironment';
import {
    DatasourceOptions,
    selectedDatasourceState,
} from '~/state/atoms/viewerAtoms';
import AppConfig from '~/utils/appconfig';
import AzureFileBrowser from './AzureDicomDownload/AzureFileBrowser';

function DatasourceTabs() {
    const { initializeEnvironment } = useViewportEnvironment();
    const { processImportedFiles } = useProcessDicomFiles();

    const isAuthenticatedToAzure = useIsAuthenticated();
    const { instance } = useMsal();
    const [value, setValue] = useRecoilState(selectedDatasourceState);
    const handleTabChange = (_: unknown, newValue: DatasourceOptions) => {
        setValue(newValue);
    };

    const handleFilesDownloaded = async (downloadedFiles: File[]) => {
        if (downloadedFiles[0]) {
            const files = Array.from(downloadedFiles);
            const importedItems = await processImportedFiles(files);
            if (importedItems) {
                initializeEnvironment(importedItems);
            }
        }
    };

    const handleLogout = () => {
        instance.logout();
    };

    const allTabsConfig = [
        {
            label: <FormattedMessage id="localFiles" />,
            value: DatasourceOptions.LOCAL,
            condition: true,
            content: <LocalFilesExplorerTab />,
        },
        {
            label: 'Server',
            value: DatasourceOptions.SERVER,
            condition: AppConfig.accessRightKey === 'development',
            content: <Box />,
        },
        {
            label: <FormattedMessage id="azureFiles" />,
            value: DatasourceOptions.REMOTE,
            condition: AppConfig.accessRightKey === 'development',
            content: isAuthenticatedToAzure ? (
                <Fragment>
                    <Box
                        display="flex"
                        justifyContent="space-between"
                        alignItems="center"
                        right={0}
                    >
                        <Typography>
                            <FormattedMessage id="azureStorage" />
                        </Typography>
                        <Tooltip
                            title={<FormattedMessage id="logoutAzure" />}
                            placement="right"
                        >
                            <IconButton onClick={handleLogout}>
                                <LogOut size={16} />
                            </IconButton>
                        </Tooltip>
                    </Box>
                    <AzureFileBrowser onDownloadFiles={handleFilesDownloaded} />
                </Fragment>
            ) : (
                <ConnectAzure />
            ),
        },
    ];

    const visibleTabs = allTabsConfig.filter(
        (tabConfig) => tabConfig.condition
    );

    return (
        <Fragment>
            <Tabs
                value={value}
                onChange={handleTabChange}
                indicatorColor="primary"
                textColor="inherit"
                variant="fullWidth"
                sx={{
                    borderBottom: (theme) =>
                        `1px solid ${theme.palette.divider}`,
                }}
            >
                {visibleTabs.map((tabConfig) => (
                    <Tab
                        key={tabConfig.value}
                        label={tabConfig.label}
                        value={tabConfig.value}
                    />
                ))}
            </Tabs>
            <Box mt={2}>
                {visibleTabs.map((tab) => 
                    value === tab.value ? (
                        <Box key={tab.value} role="tabpanel">
                            {tab.content}
                        </Box>
                    ) : null
                )}
            </Box>
        </Fragment>
    );
}

export default DatasourceTabs;
